// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for creating a new SRM request with document uploads grouped by agent.
 * Each agent contributes its own slots; slots are flattened across agents.
 * Backend auto-generates the request ID (e.g., SRM-1) from workspace request_prefix.
 */

import { useState, useCallback, useMemo, type FC } from 'react'
import { Loader2, Lock, Shield, Accessibility } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
} from '@/components/ui'
import { toast } from 'sonner'
import type { NodeApiClient } from '@/app-shell/services'
import type { UploadSlot } from '@/subsystems/documents'
import { SlotUploadGrid, type SlotFeatureGroup } from '@/subsystems/documents'
import { Input, Label } from '@/components/ui'
import type { Agent } from '@/subsystems/agents'

/** SRM-specific track display config (icons, colors) */
const TRACK_DISPLAY: Record<string, {
  icon: typeof Lock
  borderClass: string
  iconClass: string
}> = {
  'privacy-review': {
    icon: Lock,
    borderClass: 'border-l-4 border-l-chart-2',
    iconClass: 'text-chart-2',
  },
  'security-review': {
    icon: Shield,
    borderClass: 'border-l-4 border-l-chart-1',
    iconClass: 'text-chart-1',
  },
  'accessibility-review': {
    icon: Accessibility,
    borderClass: 'border-l-4 border-l-chart-3',
    iconClass: 'text-chart-3',
  },
}

interface NewRequestDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  agents: Agent[]
  slotsEnabled: boolean
  onSuccess: () => void
}

interface SlotFile {
  slotId: string
  file: File
}

interface CreateDocumentApiResponse {
  groupId?: string
}

export const NewRequestDialog: FC<NewRequestDialogProps> = ({
  open,
  onOpenChange,
  apiClient,
  orgId,
  wsId,
  agents,
  slotsEnabled,
  onSuccess,
}) => {
  const [slotFiles, setSlotFiles] = useState<SlotFile[]>([])
  const [additionalSlots, setAdditionalSlots] = useState<UploadSlot[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [externalRef, setExternalRef] = useState('')
  const [title, setTitle] = useState('')

  const uploadSlots: UploadSlot[] = useMemo(() => {
    if (!slotsEnabled) return []
    return [...agents.flatMap((a) => a.slots), ...additionalSlots]
  }, [agents, slotsEnabled, additionalSlots])

  const featureGroups: SlotFeatureGroup[] = useMemo(() => {
    if (!slotsEnabled) return []
    return agents
      .filter((a) => a.slots.length > 0 || a.allowAdditionalSlots)
      .map((a) => {
        const display = TRACK_DISPLAY[a.agentId]
        const adHocIds = additionalSlots
          .filter((s) => s.id.startsWith(`${a.agentId}-`))
          .map((s) => s.id)
        return {
          key: a.agentId,
          label: a.label,
          icon: display?.icon,
          slotIds: [...a.slots.map((s) => s.id), ...adHocIds],
          borderClass: display?.borderClass,
          iconClass: display?.iconClass,
          allowAdditional: a.allowAdditionalSlots,
        }
      })
  }, [agents, slotsEnabled, additionalSlots])

  // Build a documentsBySlot map from selected files (for preview in grid)
  const documentsBySlot = useMemo(() => {
    const map = new Map<string, { documentId: string; latestFilename: string; latestSizeBytes: number }>()
    for (const sf of slotFiles) {
      map.set(sf.slotId, {
        documentId: sf.slotId,
        latestFilename: sf.file.name,
        latestSizeBytes: sf.file.size,
      })
    }
    return map
  }, [slotFiles])

  const handleSlotUpload = useCallback(async (file: File, slotId: string) => {
    setSlotFiles(prev => {
      const existing = prev.filter(sf => sf.slotId !== slotId)
      return [...existing, { slotId, file }]
    })
  }, [])

  const handleSlotDelete = useCallback(async (slotId: string) => {
    setSlotFiles(prev => prev.filter(sf => sf.slotId !== slotId))
    setAdditionalSlots(prev => prev.filter(s => s.id !== slotId))
  }, [])

  const handleAddAdditional = useCallback(
    async (groupKey: string, label: string, file: File) => {
      const slug = label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        || 'doc'
      let slotId = `${groupKey}-${slug}`
      let n = 2
      const existingIds = new Set([
        ...agents.flatMap((a) => a.slots.map((s) => s.id)),
        ...additionalSlots.map((s) => s.id),
      ])
      while (existingIds.has(slotId)) {
        slotId = `${groupKey}-${slug}-${n++}`
      }
      setAdditionalSlots((prev) => [...prev, { id: slotId, label }])
      setSlotFiles((prev) => [...prev, { slotId, file }])
    },
    [agents, additionalSlots],
  )

  const handleSubmit = useCallback(async () => {
    if (slotFiles.length === 0) {
      setError('At least one document is required')
      return
    }

    setIsUploading(true)
    setError(null)

    try {
      const axios = apiClient.getAxiosInstance()
      let assignedGroupId: string | undefined
      const trimmedRef = externalRef.trim() || undefined
      const trimmedTitle = title.trim() || undefined

      for (const { slotId, file } of slotFiles) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('name', file.name)
        // Pass group_id from first upload for subsequent files in same request
        if (assignedGroupId) {
          formData.append('group_id', assignedGroupId)
        } else if (trimmedTitle) {
          formData.append('title', trimmedTitle)
        }
        formData.append('slot_id', slotId)
        if (trimmedRef) {
          formData.append('external_ref', trimmedRef)
        }

        const response = await axios.post<CreateDocumentApiResponse>(
          `/org/${orgId}/ws/${wsId}/srm/documents`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )

        // Capture group_id from first upload so subsequent files share the same request
        if (!assignedGroupId && response.data.groupId) {
          assignedGroupId = response.data.groupId
        }
      }

      setSlotFiles([])
      setAdditionalSlots([])
      setExternalRef('')
      setTitle('')
      onOpenChange(false)
      onSuccess()

      if (assignedGroupId) {
        toast.success(`Created ${assignedGroupId}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setIsUploading(false)
    }
  }, [apiClient, slotFiles, orgId, wsId, externalRef, title, onOpenChange, onSuccess])

  const handleClose = useCallback(() => {
    if (!isUploading) {
      setSlotFiles([])
      setAdditionalSlots([])
      setExternalRef('')
      setTitle('')
      setError(null)
      onOpenChange(false)
    }
  }, [isUploading, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!w-[720px] !max-w-[90vw] max-h-[90vh] overflow-y-auto" data-testid="new-request-modal">
        <DialogHeader>
          <DialogTitle>New Request</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="request-title">Title</Label>
            <Input
              id="request-title"
              data-testid="request-title"
              placeholder="e.g., Zoom"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isUploading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="external-ref">External Reference</Label>
            <Input
              id="external-ref"
              data-testid="external-ref"
              value={externalRef}
              onChange={(e) => setExternalRef(e.target.value)}
              disabled={isUploading}
            />
          </div>

          {slotsEnabled && (
            <SlotUploadGrid
              slots={uploadSlots}
              featureGroups={featureGroups}
              documentsBySlot={documentsBySlot}
              onUpload={handleSlotUpload}
              onDelete={handleSlotDelete}
              onAddAdditional={handleAddAdditional}
              disabled={isUploading}
            />
          )}

          {error && (
            <div className="text-sm text-destructive" data-testid="new-request-error" role="alert">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading} data-testid="new-request-cancel">
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isUploading} data-testid="new-request-submit">
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              'Create Request'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
